import React from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
} from "react-table";
import { Button, InputNumber, Select } from "antd";
import "./table.css";
import { GlobalFilter } from "./GlobalFilter";
import { ExportButtons } from "./ExportButtons";

const Table = ({ columns, data, exportFilename }) => {
  const hiddenColumns = columns
    .filter((col) => col.isVisible === false)
    .map((col) => col.accessor);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    setGlobalFilter,
    gotoPage,
    pageCount,
    setPageSize,
    prepareRow,
    rows,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns,
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;
  const firstRow = pageIndex * pageSize + 1;
  const lastRow = Math.min(pageIndex * pageSize + pageSize, data.length);

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        <ExportButtons columns={columns} rows={rows} filename={exportFilename} />
      </div>
      <div className="my-3" style={{ overflowX: "auto" }}>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={hgKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...headerProps } = column.getHeaderProps(column.getSortByToggleProps());
                    return (
                      <th key={colKey} {...headerProps}>
                        {column.render("Header")}
                        <span>
                          {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <tr key={rowKey} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...cellProps}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ul className="flex justify-around items-center">
        <li>
          <span className="inline-block align-middle">
            Showing {firstRow} - {lastRow} of {data.length} items
          </span>
        </li>
        <li title="First Page">
          <Button disabled={!canPreviousPage} onClick={() => gotoPage(0)}>{"<<"}</Button>
        </li>
        <li title="Previous Page">
          <Button disabled={!canPreviousPage} onClick={previousPage}>{"<"}</Button>
        </li>
        <li title="Next Page">
          <Button disabled={!canNextPage} onClick={nextPage}>{">"}</Button>
        </li>
        <li title="Last Page">
          <Button disabled={!canNextPage} onClick={() => gotoPage(pageCount - 1)}>{">>"}</Button>
        </li>
        <li>
          <span className="inline-block align-middle mx-2">Go to</span>
          <InputNumber
            min={1}
            max={pageOptions.length}
            style={{ width: 50 }}
            value={pageIndex + 1}
            onChange={(value) => gotoPage(value ? Number(value) - 1 : 0)}
          />
          <span className="inline-block align-middle mx-2">Page</span>
        </li>
        <li>
          <Select value={pageSize} onChange={(value) => setPageSize(Number(value))}>
            {[5, 10, 25, 50].map((size) => (
              <Select.Option key={size} value={size}>Show {size}</Select.Option>
            ))}
          </Select>
        </li>
      </ul>
    </>
  );
};

export default Table;
